"use client";

import {
  type HomePanelConfig,
  type HomePanelItem,
  type HomePanelItemGroup,
  type HomePanelItemLink,
  type HomePanelItemSend,
  isHomePanelItemGroup,
  isHomePanelItemLink,
  isHomePanelItemSend,
} from "../model";
import { cn } from "@deputy-ai/common";
import { AvatarImage, Avatar } from "@deputy-ai/ui/avatar/Avatar";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { AnimatePresence, easeOut, motion } from "framer-motion";
import { createContext, useContext } from "react";
import { BiSolidSend } from "react-icons/bi";
import { IoChevronDown } from "react-icons/io5";
import { RxLink1 } from "react-icons/rx";

interface RouteWebchatMessageParams {
  query?: string;
  allowAnonymous: boolean;
}

interface HomePanelProps {
  panel: HomePanelConfig;
  storageConversationId?: string;
  routeWebChatMesssage?: (params: RouteWebchatMessageParams) => void;
  groupDefaultOpen?: boolean;
}

const HomePanelContext = createContext<HomePanelProps | undefined>(undefined);

export const HomePanel = (props: HomePanelProps) => {
  return (
    <HomePanelContext.Provider value={props}>
      <HomePanelContent />
    </HomePanelContext.Provider>
  );
};

const HomePanelContent = () => {
  const context = useContext(HomePanelContext);
  if (!context) {
    return null;
  }
  const { panel, storageConversationId } = context;

  if (panel.visibilityRule === "no_chat_hide" && !storageConversationId) {
    return null;
  }
  if (panel.visibilityRule === "chat_hide" && storageConversationId) {
    return null;
  }
  return (
    <div className="flex flex-col gap-4">
      {(panel.title || panel.icon) && (
        <div className="panel-title flex h-8 items-center gap-2 px-4 text-base font-bold">
          {panel.icon && (
            <Avatar size="sm">
              <AvatarImage src={panel.icon} />{" "}
            </Avatar>
          )}
          <span>{panel.title}</span>
        </div>
      )}
      <div
        className={cn("panel-group flex flex-col rounded-xl bg-black/5 py-2")}
      >
        <ItemListPannel
          items={panel.items}
          allowAnonymous={panel.allowAnonymous ?? false}
        />
      </div>
    </div>
  );
};

interface ItemListPannelProps {
  allowAnonymous: boolean;
  items: HomePanelItem[];
}

const ItemListPannel = ({ items, allowAnonymous }: ItemListPannelProps) => {
  return (
    <div className="mx-auto w-full divide-y divide-white/5 rounded-xl">
      {items.map((item, index) => (
        <ItemPannel key={index} item={item} allowAnonymous={allowAnonymous} />
      ))}
    </div>
  );
};

const ItemPannel = ({
  item,
  allowAnonymous,
}: {
  item: HomePanelItem;
  allowAnonymous: boolean;
}) => {
  return (
    <>
      {typeof item === "string" && (
        <ItemSendPannel
          item={{ type: "send", text: item }}
          allowAnonymous={allowAnonymous}
        />
      )}
      {isHomePanelItemSend(item) && (
        <ItemSendPannel item={item} allowAnonymous={allowAnonymous} />
      )}
      {isHomePanelItemGroup(item) && (
        <ItemGroupPannel item={item} allowAnonymous={allowAnonymous} />
      )}
      {isHomePanelItemLink(item) && <ItemLinkPannel item={item} />}
    </>
  );
};

interface ItemSendPannelProps {
  allowAnonymous: boolean;
  item: HomePanelItemSend;
}

const ItemSendPannel = ({ item, allowAnonymous }: ItemSendPannelProps) => {
  const context = useContext(HomePanelContext);
  if (!context) {
    return null;
  }

  const { storageConversationId, routeWebChatMesssage } = context;

  if (item.hideIfNoChat && !storageConversationId) {
    return null;
  }

  return (
    <div
      className={btnLinkClassName}
      onClick={() =>
        routeWebChatMesssage?.({
          query: item.disableQuery ? undefined : item.text,
          allowAnonymous,
        })
      }
    >
      {item.text}
      <BiSolidSend className={btnIconClassName} />
    </div>
  );
};

interface ItemGroupPannelProps {
  allowAnonymous: boolean;
  item: HomePanelItemGroup;
}

const ItemGroupPannel = ({ item, allowAnonymous }: ItemGroupPannelProps) => {
  const context = useContext(HomePanelContext);
  if (!context) {
    return null;
  }
  const { groupDefaultOpen } = context;
  return (
    <Disclosure as="div" className="w-full" defaultOpen={groupDefaultOpen}>
      {({ open }) => (
        <>
          <DisclosureButton
            className={cn(
              "panel-group-toggle group flex w-full items-center justify-between px-4 py-2",
              btnHoverClassName
            )}
          >
            <span className="text-sm/6 font-medium">{item.title}</span>
            <IoChevronDown className={btnIconClassName} />
          </DisclosureButton>
          <div className="overflow-hidden">
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: -24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.2, ease: easeOut }}
                className="origin-top"
              >
                {open && (
                  <DisclosurePanel
                    transition
                    className="origin-top transition duration-200 ease-out data-closed:-translate-y-6 data-closed:opacity-0"
                  >
                    <ItemListPannel
                      items={item.items}
                      allowAnonymous={allowAnonymous}
                    />
                  </DisclosurePanel>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </>
      )}
    </Disclosure>
  );
};

interface ItemLinkPannelProps {
  item: HomePanelItemLink;
}

const ItemLinkPannel = ({ item }: ItemLinkPannelProps) => {
  return (
    <a
      className={btnLinkClassName}
      href={item.url}
      target="_blank"
      rel="noreferrer"
    >
      {item.text}
      <RxLink1 className={btnIconClassName} />
    </a>
  );
};

const btnHoverClassName = "hover:bg-black/10 transition-all duration-300";

const btnLinkClassName = cn(
  "inline-flex cursor-pointer items-center justify-center bg-transparent font-medium whitespace-nowrap disabled:opacity-50",
  "w-full justify-between px-4 py-2 text-sm/6",
  btnHoverClassName,
  "panel-link"
);

const btnIconClassName = "size-5 group-data-open:rotate-180";
